import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import api from '../lib/api';
import { ShoppingCart, User, Bell, LogOut, Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useAdminStoreView } from '../context/AdminStoreViewContext';
import { BatchtrueLockup } from '@/components/assay/BatchtrueMark';

const LINK_CLASS =
  'text-[0.82rem] text-muted-foreground transition-colors hover:text-foreground ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ' +
  'focus-visible:ring-offset-background rounded-sm';

const SHEET_LINK_CLASS =
  'rounded-sm px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** Mark plus wordmark, from the shared Assay lockup. */
function Wordmark({ className = '' }) {
  return <BatchtrueLockup className={`text-foreground ${className}`} markClassName="h-7 w-7" />;
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const { isStoreView } = useAdminStoreView();
  const { totalItems, setDrawerOpen } = useCart();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    if (user) {
      api.get('/notifications', { params: { limit: 1 } }).then(res => {
        setUnreadCount(res.data.unread || 0);
      }).catch(() => {});
    }
  }, [user]);

  // The hairline only appears once the page has moved, so the header sits flat at rest.
  useEffect(() => {
    const onScroll = () => setStuck(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav
      className={`sticky top-0 z-50 border-b bg-background/85 backdrop-blur-md transition-colors duration-300 ${
        stuck ? 'border-border' : 'border-transparent'
      }`}
      data-testid="navbar"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div
          className={`flex items-center justify-between gap-4 transition-all duration-300 ${
            stuck ? 'h-14' : 'h-16'
          }`}
        >
          <Link
            to="/"
            className="rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            data-testid="nav-logo"
            aria-label="Batchtrue Wholesale home"
          >
            <Wordmark />
          </Link>

          <div className="hidden items-center gap-6 md:flex">
            <Link to="/catalog" className={LINK_CLASS} data-testid="nav-catalog">Catalog</Link>
            {user && (
              <>
                <Link to="/account/orders" className={LINK_CLASS}>Orders</Link>
                <Link to="/account/tickets" className={LINK_CLASS}>Support</Link>
                <Link to="/account" className={LINK_CLASS} data-testid="nav-account">Account</Link>
              </>
            )}
            {user?.role === 'admin' && !isStoreView && (
              <Link
                to="/admin"
                className={`${LINK_CLASS} font-medium text-primary hover:text-primary`}
                data-testid="nav-admin"
              >
                Admin
              </Link>
            )}
          </div>

          <div className="flex items-center gap-1 sm:gap-2">
            <Button
              variant="ghost"
              size="icon"
              type="button"
              className="relative min-h-11 min-w-11"
              data-testid="nav-cart"
              onClick={() => setDrawerOpen(true)}
              aria-label={totalItems > 0 ? `Open cart, ${totalItems} items` : 'Open cart'}
            >
              <ShoppingCart className="h-5 w-5" />
              {totalItems > 0 && (
                <motion.span
                  key={totalItems}
                  initial={{ scale: 1.35 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 15 }}
                  className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[0.62rem] font-semibold text-primary-foreground"
                >
                  {totalItems}
                </motion.span>
              )}
            </Button>

            {user && (
              <Link to="/account/notifications" data-testid="nav-notifications" aria-label="Notifications">
                <Button variant="ghost" size="icon" className="relative min-h-11 min-w-11" aria-label="Notifications">
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-[0.62rem] font-semibold text-destructive-foreground">
                      {unreadCount}
                    </span>
                  )}
                </Button>
              </Link>
            )}

            {user ? (
              <div className="hidden items-center gap-1 md:flex">
                <Link to="/account">
                  <Button variant="ghost" size="sm" className="text-[0.82rem] font-normal text-muted-foreground">
                    <User className="h-4 w-4" />
                    {user.name || user.email}
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => { logout(); navigate('/'); }}
                  data-testid="nav-logout"
                  aria-label="Sign out"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="hidden items-center gap-2 md:flex">
                <Link to="/login">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-full text-[0.82rem] font-normal text-muted-foreground"
                    data-testid="nav-login"
                  >
                    Sign in
                  </Button>
                </Link>
                <Link to="/register">
                  <Button
                    size="sm"
                    className="rounded-full px-5 text-[0.78rem] font-semibold tracking-[0.02em]"
                    data-testid="nav-register"
                  >
                    Request access
                  </Button>
                </Link>
              </div>
            )}

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" className="min-h-11 min-w-11" aria-label="Open menu"><Menu className="h-5 w-5" /></Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 border-border bg-background">
                <SheetHeader className="sr-only">
                  <SheetTitle>Menu</SheetTitle>
                  <SheetDescription>Site navigation links</SheetDescription>
                </SheetHeader>
                <div className="mt-10 flex flex-col gap-1">
                  <Wordmark className="mb-4 px-3" />
                  <Link to="/catalog" onClick={() => setMobileOpen(false)} className={SHEET_LINK_CLASS}>Catalog</Link>
                  {user ? (
                    <>
                      <Link to="/account" onClick={() => setMobileOpen(false)} className={SHEET_LINK_CLASS}>My account</Link>
                      <Link to="/account/orders" onClick={() => setMobileOpen(false)} className={SHEET_LINK_CLASS}>Orders</Link>
                      <Link to="/account/tickets" onClick={() => setMobileOpen(false)} className={SHEET_LINK_CLASS}>Support</Link>
                      {user.role === 'admin' && !isStoreView && (
                        <Link to="/admin" onClick={() => setMobileOpen(false)} className={`${SHEET_LINK_CLASS} text-primary`}>Admin panel</Link>
                      )}
                      <button
                        type="button"
                        onClick={() => { logout(); navigate('/'); setMobileOpen(false); }}
                        className={`${SHEET_LINK_CLASS} text-left text-destructive`}
                      >
                        Sign out
                      </button>
                    </>
                  ) : (
                    <>
                      <Link to="/login" onClick={() => setMobileOpen(false)} className={SHEET_LINK_CLASS}>Sign in</Link>
                      <Link to="/register" onClick={() => setMobileOpen(false)} className="mt-2">
                        <Button className="w-full rounded-full text-[0.78rem] font-semibold">Request access</Button>
                      </Link>
                    </>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
}
